/**
 * # Visible-Set Preview Cache (PR-V2)
 *
 * Memoises the per-id `EntityPreview` payload produced from a
 * `DrawingEntity` lookup so consecutive viewport frames don't have to
 * rebuild every visible entity's preview from scratch. The Three.js
 * renderer feeds the result of `buildShellEntityPreview(...)` through
 * `entityToRenderable(...)`; both passes are cheap individually but
 * scale linearly with the visible-set size and run on every camera
 * recompute. With a 685k-entity drawing zoomed-out we'd burn the work
 * once per frame even though the preview output is identical until the
 * underlying entity mutates.
 *
 * # Eviction
 *
 * Plain LRU keyed by id. Map preserves insertion order; on every `get`
 * we delete + re-set so the most-recently-used entry sits at the tail.
 * Once the size exceeds `maxEntries`, we drop the head (oldest entry)
 * — a single Map.delete on the first iterated key.
 *
 * # Invalidation
 *
 * Mutations (`COMMAND_PATCH_ENTITY`, paste, geometry edits) call
 * `invalidate(id)` to drop one entry; full re-imports call `clear()`.
 * The cache holds no references to the underlying DrawingEntity so
 * cleared entries become eligible for GC immediately.
 *
 * # Memory
 *
 * Cache stores `EntityPreview` objects only — typically a few hundred
 * bytes each (line endpoints, mtext runs, hatch loops, ...). At
 * `maxEntries = 25_000` the worst-case retention is ~12 MB. Sized for
 * typical viewport visible-set (5-10k) with pan/zoom headroom; the
 * hit-rate counter on `getStats()` lets the mem-profile harness
 * re-tune adaptively if needed.
 */

import type { EntityPreview } from "@dxf-ai-editor/contracts";

export interface VisibleSetCacheConfig {
  /**
   * Soft cap on retained entries. The cache evicts oldest-first
   * whenever a write would push `size` past this limit. A value of
   * `Infinity` disables eviction (useful for tests).
   */
  maxEntries: number;
}

/**
 * Builder callback shape: invoked on cache miss. Returning `null`
 * indicates the underlying entity is unresolvable (e.g. removed since
 * the spatial index emitted its id) — the null result is NOT cached so
 * the next call will retry.
 */
export type PreviewBuilder = (id: string) => EntityPreview | null;

export class VisibleSetCache {
  private readonly entries: Map<string, EntityPreview>;
  private readonly maxEntries: number;
  private hits = 0;
  private misses = 0;
  private evictions = 0;

  constructor(config: VisibleSetCacheConfig) {
    if (!Number.isFinite(config.maxEntries) && config.maxEntries !== Infinity) {
      throw new Error(
        "[visibleSetCache] maxEntries must be a finite number or Infinity",
      );
    }
    if (config.maxEntries <= 0) {
      throw new Error("[visibleSetCache] maxEntries must be > 0");
    }
    this.entries = new Map();
    this.maxEntries = config.maxEntries;
  }

  /**
   * Returns the memoised preview for `id`. On miss, invokes `build(id)`
   * and caches the non-null result. Touches the entry's LRU position
   * either way so hot ids never get evicted.
   */
  getOrBuild(id: string, build: PreviewBuilder): EntityPreview | null {
    const existing = this.entries.get(id);
    if (existing !== undefined) {
      this.hits++;
      // LRU bump: re-insert so the entry sits at the tail of Map's
      // insertion-order iteration.
      this.entries.delete(id);
      this.entries.set(id, existing);
      return existing;
    }
    this.misses++;
    const built = build(id);
    if (built === null) {
      // Don't cache null — the underlying entity may reappear on a
      // later frame after a state mutation.
      return null;
    }
    this.entries.set(id, built);
    this.evictIfNeeded();
    return built;
  }

  /** Drop a single id (mutation path). No-op if absent. */
  invalidate(id: string): void {
    this.entries.delete(id);
  }

  /** Drop everything (import / re-init path). */
  clear(): void {
    this.entries.clear();
  }

  /** Diagnostic accessor — exposed for unit tests. */
  get size(): number {
    return this.entries.size;
  }

  /**
   * Direct read without LRU bump or build callback. Returns `undefined`
   * for misses. Used by diagnostic / test code; production paths go
   * through `getOrBuild`.
   */
  peek(id: string): EntityPreview | undefined {
    return this.entries.get(id);
  }

  private evictIfNeeded(): void {
    if (this.maxEntries === Infinity) return;
    while (this.entries.size > this.maxEntries) {
      // Map iterates in insertion order — the first key is the oldest
      // (LRU) entry. Delete it and continue until we're back under the
      // soft cap (loop runs at most one iteration in normal flow but
      // is defensive against a smaller cap being installed mid-life).
      const oldest = this.entries.keys().next();
      if (oldest.done) return;
      this.entries.delete(oldest.value);
      this.evictions++;
    }
  }

  /** Cumulative diagnostics for the mem-profile harness. */
  getStats(): { hits: number; misses: number; evictions: number; size: number; hitRate: number } {
    const total = this.hits + this.misses;
    return {
      hits: this.hits,
      misses: this.misses,
      evictions: this.evictions,
      size: this.entries.size,
      hitRate: total === 0 ? 0 : this.hits / total,
    };
  }
}

/**
 * Convenience factory matching the pattern used by other modules
 * (`createSpatialIndex`, `createEntityMetadataIndex`, ...).
 */
export function createVisibleSetCache(
  config: VisibleSetCacheConfig,
): VisibleSetCache {
  return new VisibleSetCache(config);
}
